'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
const AMBIENT_INTENSITY = 0.7;
const KEY_INTENSITY = 0.7;
const FILL_INTENSITY = 0.35;
/** Fraction of the surrounding light kept while a spotlight emphasizes part of the scene. */
const EMPHASIS_SURROUND = 0.3;
const SPOTLIGHT_INTENSITY = 2.4;
/** Margin beyond the emphasized bounds so the cone's soft edge falls outside the target. */
const SPOTLIGHT_MARGIN = 1.35;
const SPOTLIGHT_PENUMBRA = 0.45;
const FADE_SECONDS = 0.25;
/**
 * Owns every scene light. Emphasis dims the surrounding light and casts a camera-borne spotlight,
 * so emphasis reads as attention rather than as activation's color change.
 */
export function SceneLighting(props) {
    const camera = useThree(state => state.camera);
    const scene = useThree(state => state.scene);
    const ambient = useRef(null);
    const key = useRef(null);
    const fill = useRef(null);
    const spot = useRef(null);
    const level = useRef(0);
    const lastSpotlight = useRef(props.spotlight);
    if (props.spotlight)
        lastSpotlight.current = props.spotlight;
    useEffect(() => {
        const light = spot.current;
        if (!light)
            return;
        scene.add(light.target);
        return () => { scene.remove(light.target); };
    }, [scene]);
    useFrame((_, delta) => {
        const goal = props.spotlight ? 1 : 0;
        level.current += (goal - level.current) * (1 - Math.exp(-delta / FADE_SECONDS));
        if (Math.abs(goal - level.current) < 1e-3)
            level.current = goal;
        const surround = 1 - (1 - EMPHASIS_SURROUND) * level.current;
        if (ambient.current)
            ambient.current.intensity = AMBIENT_INTENSITY * surround;
        if (key.current)
            key.current.intensity = KEY_INTENSITY * surround;
        if (fill.current)
            fill.current.intensity = FILL_INTENSITY * surround;
        const light = spot.current;
        const target = lastSpotlight.current;
        if (!light)
            return;
        if (!target || level.current === 0) {
            light.intensity = 0;
            return;
        }
        light.color.set(target.color);
        light.intensity = SPOTLIGHT_INTENSITY * level.current;
        camera.getWorldPosition(light.position);
        light.target.position.set(...target.center);
        light.target.updateMatrixWorld();
        const distance = light.position.distanceTo(light.target.position);
        light.angle = Math.min(Math.PI / 3, Math.max(0.01, Math.atan((target.radius * SPOTLIGHT_MARGIN) / Math.max(distance, 1e-6))));
    });
    return (_jsxs(_Fragment, { children: [_jsx("ambientLight", { ref: ambient, intensity: AMBIENT_INTENSITY }), _jsx("directionalLight", { ref: key, position: [6, 10, 8], intensity: KEY_INTENSITY }), _jsx("directionalLight", { ref: fill, position: [-8, 4, -6], intensity: FILL_INTENSITY }), _jsx("spotLight", { ref: spot, intensity: 0, decay: 0, distance: 0, penumbra: SPOTLIGHT_PENUMBRA })] }));
}
